/**
 * Recently viewed products ("Последно разгледани").
 *
 * Tracks opened product ids in localStorage (newest first, capped) and
 * fills the recently-viewed section by fetching the search page with an
 * `id:… OR id:…` query and this section's section_id — the section then
 * renders the matching product cards from search.results server-side.
 * The current product is recorded but never shown to itself. The section
 * wrapper stays hidden until there is something to show.
 */
if (!customElements.get('recently-viewed-products')) {
  customElements.define(
    'recently-viewed-products',
    class RecentlyViewedProducts extends HTMLElement {
      static STORAGE_KEY = 'bultex:recently-viewed';
      static MAX_STORED = 12;

      connectedCallback() {
        const currentId = this.dataset.currentProductId;
        let ids = this.readIds();

        if (currentId) {
          ids = [currentId, ...ids.filter((id) => id !== currentId)].slice(
            0,
            RecentlyViewedProducts.MAX_STORED
          );
          try {
            localStorage.setItem(RecentlyViewedProducts.STORAGE_KEY, JSON.stringify(ids));
          } catch {
            // storage full/blocked — tracking is best-effort
          }
        }

        this.idsToShow = ids
          .filter((id) => id !== currentId)
          .slice(0, parseInt(this.dataset.limit || '8', 10));
        if (!this.idsToShow.length) return;

        // The wrapper starts hidden (display:none), so the element has no
        // box and IntersectionObserver would never fire — fetch on idle
        // instead; it is a single cached section request.
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => this.load(), { timeout: 2000 });
        } else {
          setTimeout(() => this.load(), 200);
        }
      }

      readIds() {
        try {
          const stored = JSON.parse(localStorage.getItem(RecentlyViewedProducts.STORAGE_KEY));
          return Array.isArray(stored) ? stored.map(String) : [];
        } catch {
          return [];
        }
      }

      async load() {
        const query = this.idsToShow.map((id) => `id:${id}`).join(' OR ');
        const url = `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}&type=product&section_id=${this.dataset.sectionId}`;

        try {
          const response = await fetch(url);
          if (!response.ok) return;
          const text = await response.text();
          const html = new DOMParser().parseFromString(text, 'text/html');
          const fetched = html.querySelector('recently-viewed-products');
          if (!fetched || !fetched.querySelector('.slider__slide')) return;

          this.innerHTML = fetched.innerHTML;
          this.reorderSlides();

          const wrapper = this.closest('[data-recently-viewed-wrapper]');
          if (wrapper) wrapper.hidden = false;
        } catch {
          // network hiccup — leave the section hidden
        }
      }

      /* Search returns its own ranking; restore most-recently-viewed-first. */
      reorderSlides() {
        const list = this.querySelector('ul.slider');
        if (!list) return;
        const order = this.idsToShow;
        const slides = [...list.children];
        slides
          .sort((a, b) => {
            const idA = a.querySelector('[data-product-id]')?.dataset.productId;
            const idB = b.querySelector('[data-product-id]')?.dataset.productId;
            return order.indexOf(idA) - order.indexOf(idB);
          })
          .forEach((slide) => list.appendChild(slide));
      }
    }
  );
}
